import React from "react";
import { CircleAlert, GripVertical } from "lucide-react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Tooltip from "../ui/Tooltip";
import SlotButton from "./SlotButton";
import { BtNodeDisc, STATUS_COLORS, getCategory } from "./nodeAppearance";
import { getBehaviorTrainedModel } from "../../behavior/trainedModels";
import PanelDeleteIconButton from "../draggable-panels/PanelDeleteIconButton";

const KIND_LABELS = {
  sequence: "Sequence",
  selector: "Fallback",
  retry: "Retry",
  repeat: "Repeat",
  ifThenElse: "If / Then / Else",
  servoPickPlace: "Pick & place",
  moveEeToXyz: "Move EE → XYZ",
  moveEeByOffset: "Move EE offset",
  runTrainedModel: "Trained model",
  openGripper: "Open gripper",
  closeGripper: "Close gripper",
  goToKeyframe: "Keyframe",
  wait: "Wait",
  randomizeObjectPose: "Randomize object",
  setObjectPose: "Set object pose",
  resetToBaseline: "Reset baseline",
  markSuccess: "Mark success",
  markFailure: "Mark failure",
  conditionObjectGrasped: "If object lifted",
  conditionEeNear: "If EE near",
  conditionObjectNear: "If object near",
  conditionTimeElapsed: "If time elapsed",
};

function bodyLabel(bodiesByUuid, uuid) {
  if (!uuid) return "—";
  const b = bodiesByUuid[uuid];
  if (b?.name) return b.name;
  return String(uuid).slice(0, 8);
}

function xyz(n) {
  const f = (v) => (Number.isFinite(v) ? v.toFixed(2) : "?");
  return `(${f(n.x)}, ${f(n.y)}, ${f(n.z)})`;
}

function leafSummary(node, bodiesByUuid) {
  switch (node.kind) {
    case "servoPickPlace": {
      const pick = bodyLabel(bodiesByUuid, node.pickBodyUuid);
      const drop = node.dropBodyUuid ? bodyLabel(bodiesByUuid, node.dropBodyUuid) : "—";
      return `${pick} → ${drop}`;
    }
    case "moveEeToXyz":
      return node.targetBodyUuid
        ? `${node.endEffectorRef || "ee"} → ${bodyLabel(bodiesByUuid, node.targetBodyUuid)} + ${xyz(node)}`
        : `${node.endEffectorRef || "ee"} → ${xyz(node)}`;
    case "moveEeByOffset": {
      const f = (v) => (Number.isFinite(v) ? v.toFixed(2) : "?");
      return `${node.endEffectorRef || "ee"} Δ(${f(node.dx)}, ${f(node.dy)}, ${f(node.dz)})`;
    }
    case "runTrainedModel": {
      const model = getBehaviorTrainedModel(node.modelId);
      const duration = Number.isFinite(node.durationSeconds) ? `${node.durationSeconds}s` : "?s";
      const vx = Number.isFinite(node.commandVx) ? Number(node.commandVx).toFixed(2) : "?";
      const wz = Number.isFinite(node.commandWz) ? Number(node.commandWz).toFixed(2) : "?";
      return model
        ? `${model.name} · ${duration} · vx ${vx} · wz ${wz} · ${model.robotLabel}`
        : `${node.modelId || "Unknown model"} · ${duration} · vx ${vx} · wz ${wz}`;
    }
    case "goToKeyframe":
      return node.keyframeName || "—";
    case "wait":
      return `${node.seconds}s`;
    case "randomizeObjectPose":
      return `${bodyLabel(bodiesByUuid, node.bodyUuid)} · r=${node.radiusXY}m`;
    case "setObjectPose":
      return `${bodyLabel(bodiesByUuid, node.bodyUuid)} · ${xyz(node)}`;
    case "conditionObjectGrasped":
      return `${bodyLabel(bodiesByUuid, node.bodyUuid)} · y ≥ ${node.minHeightY}`;
    case "conditionEeNear":
      return node.targetBodyUuid
        ? `${node.endEffectorRef || "ee"} → ${bodyLabel(bodiesByUuid, node.targetBodyUuid)} + ${xyz(node)} ± ${node.toleranceM}m`
        : `${node.endEffectorRef || "ee"} · ${xyz(node)} ± ${node.toleranceM}m`;
    case "conditionObjectNear":
      return node.targetBodyUuid
        ? `${bodyLabel(bodiesByUuid, node.bodyUuid)} → ${bodyLabel(bodiesByUuid, node.targetBodyUuid)} + ${xyz(node)} ± ${node.toleranceM}m`
        : `${bodyLabel(bodiesByUuid, node.bodyUuid)} · ${xyz(node)} ± ${node.toleranceM}m`;
    case "conditionTimeElapsed":
      return `≥ ${node.seconds}s`;
    default:
      return null;
  }
}

function defaultKindTitle(node) {
  if (node.kind === "runTrainedModel") {
    return getBehaviorTrainedModel(node.modelId)?.name || KIND_LABELS[node.kind] || node.kind;
  }
  return KIND_LABELS[node.kind] || node.kind;
}

function nodeTitle(node) {
  if (node.label && node.label.trim().length > 0) return node.label;
  return defaultKindTitle(node);
}

function compositeTitle(node) {
  const labelled = node.label && node.label.trim().length > 0;
  switch (node.kind) {
    case "sequence":
      return labelled ? node.label : "Sequence";
    case "selector":
      return labelled ? node.label : "Fallback";
    case "retry":
      return `${labelled ? node.label : "Retry"} \u00d7 ${node.maxAttempts}`;
    case "repeat":
      return `${labelled ? node.label : "Repeat"} \u00d7 ${node.count}`;
    case "ifThenElse":
      return labelled ? node.label : "If / Then / Else";
    default:
      return nodeTitle(node);
  }
}

function StatusDot({ status }) {
  if (!status || status === "idle") {
    return (
      <span
        className="inline-block h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: STATUS_COLORS.idle }}
        aria-hidden="true"
      />
    );
  }
  const fill = STATUS_COLORS[status] || STATUS_COLORS.idle;
  const pulse = status === "running";
  return (
    <span
      className="inline-block h-2 w-2 shrink-0 rounded-full"
      style={{
        backgroundColor: fill,
        boxShadow: pulse ? "0 0 0 3px rgba(251,191,36,0.2)" : "none",
      }}
      aria-label={`Last tick: ${status}`}
    />
  );
}

function statusIndicatorTooltip(status, needsSetup) {
  if (needsSetup) {
    return "Needs configuration: open the inspector and fill required fields before running this behavior.";
  }
  switch (status) {
    case "running":
      return "Last tick: this step was running.";
    case "success":
      return "Last tick: this step succeeded.";
    case "failure":
      return "Last tick: this step failed.";
    default:
      return "Last tick: idle (not active on the last update, or not reached yet).";
  }
}

/** Status dot, or amber warning in the same slot when the node still needs inspector/scene input. */
function StatusOrSetupIndicator({ status, needsSetup, setupMessage }) {
  const tip = setupMessage || statusIndicatorTooltip(status, needsSetup);
  return (
    <Tooltip content={tip} side="right" sideOffset={8}>
      <span
        className="inline-flex shrink-0 cursor-default items-center justify-center"
        aria-label={tip}
      >
        {needsSetup ? (
          <CircleAlert className="h-3.5 w-3.5 shrink-0 text-amber-400" aria-hidden="true" />
        ) : (
          <StatusDot status={status} />
        )}
      </span>
    </Tooltip>
  );
}

function DeleteButton({ onDelete, label = "Delete" }) {
  return (
    <Tooltip content={label} side="left">
      <PanelDeleteIconButton
        ariaLabel={label}
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="h-6 w-6 shrink-0 rounded-sm p-0 opacity-0 transition-opacity focus-visible:opacity-100 group-hover/header:opacity-100"
        iconClassName="h-3.5 w-3.5 shrink-0"
      />
    </Tooltip>
  );
}

/** Slightly lighter than canvas (~rgb(28,28,30)); neutral gray (no slate/blue bias). */
const SEQUENCE_SURFACE = "rgb(44, 44, 44)";
/** Fallback / retry / repeat / if card fill. */
const COMPOSITE_CARD_BG = "rgba(34, 34, 34, 0.92)";
/** Leaf row fill inside the tree. */
const LEAF_ROW_BG = "rgb(40, 40, 40)";
/** Default sequence column width (nested sequences use full width of parent up to this cap). */
const SEQUENCE_MAX_WIDTH_CLASS = "max-w-[400px]";

function isActiveInsertSlot(activeInsertSlot, parentId, insertIndex) {
  return (
    activeInsertSlot != null &&
    activeInsertSlot.parentId === parentId &&
    activeInsertSlot.index === insertIndex
  );
}

function SortableSequenceChildRow({ id, parentId, index, block, slotAfter }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    data: { parentId, index },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 20 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex w-full flex-col gap-1 overflow-visible">
      <div className="flex w-full min-w-0 items-start gap-2 overflow-visible">
        <Tooltip content="Drag to reorder or re-nest" side="right">
          <button
            type="button"
            className="mt-1.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-sm panel-text-helper hover:bg-neutral-800/90 hover:text-gray-300 cursor-grab active:cursor-grabbing"
            aria-label="Drag to reorder or re-nest"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        </Tooltip>
        {/* Extra left inset so selection ring / dotted borders are not clipped by flex shrink or tight padding. */}
        <div className="flex-1 overflow-visible pl-1">{block}</div>
      </div>
      {slotAfter}
    </div>
  );
}

/**
 * Shared sortable child list for sequence and selector parents. The surrounding
 * `DndContext` lives in `BehaviorTreeCanvas` so drag operations can span
 * parents (reorder stays within a `SortableContext`, but cross-parent drops
 * are handled at the top level).
 */
function SortableCompositeChildren({
  parentNode,
  children,
  allNodes,
  rootId,
  nodeStatuses,
  activeNodeId,
  bodiesByUuid,
  incompleteIds,
  setupMessages,
  onSelect,
  onDelete,
  onSlotClick,
  onReorderSequenceChildren,
  activeInsertSlot,
  depth,
}) {
  return (
    <SortableContext items={children} strategy={verticalListSortingStrategy}>
      {children.map((cid, i) => (
        <SortableSequenceChildRow
          key={cid}
          id={cid}
          parentId={parentNode.id}
          index={i}
          block={
            <BtNodeBlock
              node={allNodes[cid]}
              allNodes={allNodes}
              rootId={rootId}
              nodeStatuses={nodeStatuses}
              activeNodeId={activeNodeId}
              bodiesByUuid={bodiesByUuid}
              setupMessages={setupMessages}
              onSelect={onSelect}
              onDelete={onDelete}
              onSlotClick={onSlotClick}
              onReorderSequenceChildren={onReorderSequenceChildren}
              sequenceStepIndex={parentNode.kind === "sequence" ? i + 1 : undefined}
              activeInsertSlot={activeInsertSlot}
              depth={depth}
              incompleteIds={incompleteIds}
            />
          }
          slotAfter={
            <SlotButton
              onClick={(e) => onSlotClick(parentNode.id, i + 1, e.currentTarget)}
              variant="between"
              label="Add step"
              highlightInsert={isActiveInsertSlot(activeInsertSlot, parentNode.id, i + 1)}
            />
          }
        />
      ))}
    </SortableContext>
  );
}

/**
 * Makes a composite's whole card a drop target — dropping any dragged node
 * here re-parents the subtree into this composite (handled by the top-level
 * `DndContext` in `BehaviorTreeCanvas`).
 */
function CompositeDroppable({ compositeId, className, style, children, ...rest }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `drop-${compositeId}`,
    data: { compositeId, kind: "composite" },
  });
  const combinedStyle = isOver
    ? { ...(style || {}), boxShadow: "0 0 0 2px rgba(56,189,248,0.9)" }
    : style;
  return (
    <div ref={setNodeRef} className={className || ""} style={combinedStyle} {...rest}>
      {children}
    </div>
  );
}

/**
 * Recursive container / leaf block. Composites render a header + vertically
 * stacked children interleaved with slot buttons. Decorators render a header
 * + a single inner slot. Leaves render a pill with icon and parameter summary.
 */
export default function BtNodeBlock({
  node,
  allNodes,
  rootId,
  nodeStatuses,
  activeNodeId,
  bodiesByUuid,
  onSelect,
  onDelete,
  onSlotClick,
  /** `(parentId, fromIndex, toIndex)` — only used for sequence child reorder via drag handle. */
  onReorderSequenceChildren,
  /** 1-based position when this node is a direct child of a sequence (undefined otherwise). */
  sequenceStepIndex,
  /** When the insert popover is open: `{ parentId, index }` for the chosen slot. */
  activeInsertSlot = null,
  depth = 0,
  /** Node ids that still need inspector/scene input before a run is meaningful. */
  incompleteIds = null,
  setupMessages = null,
}) {
  if (!node) return null;

  const status = nodeStatuses?.[node.id] || "idle";
  const isActive = node.id === activeNodeId;
  const isRoot = node.id === rootId;
  const category = getCategory(node.kind);
  const inSequenceStep = sequenceStepIndex != null && sequenceStepIndex >= 1;
  /** Composites use the same disc scale as step rows inside a sequence (was 22 at root). */
  const isCompositeKind =
    node.kind === "sequence" ||
    node.kind === "selector" ||
    node.kind === "retry" ||
    node.kind === "repeat" ||
    node.kind === "ifThenElse";
  const discSize = isCompositeKind || inSequenceStep ? 32 : 22;
  const discLargeIcon = isCompositeKind || inSequenceStep;
  const needsSetup = incompleteIds?.has(node.id) ?? false;
  const setupMessage = setupMessages?.[node.id] || "";

  const selectRing = isActive ? "ring-2 ring-amber-400/70" : "ring-0";

  const headerClick = (e) => {
    e.stopPropagation();
    onSelect(node.id);
  };

  if (node.kind === "sequence" || node.kind === "selector") {
    const children = node.children || [];
    const isSequence = node.kind === "sequence";
    /** Tailwind shadow-sm equivalent */
    const sequenceDropShadow = "0 1px 2px 0 rgb(0 0 0 / 0.12)";

    const compositeRowTooltip = `${compositeTitle(node)}${needsSetup ? " · needs configuration" : ""}`;

    return (
      <CompositeDroppable
        compositeId={node.id}
        data-behavior-tree-node="true"
        data-behavior-node-id={node.id}
        className={
          isSequence
            ? `w-full ${SEQUENCE_MAX_WIDTH_CLASS} overflow-visible rounded-lg border-2 border-dotted border-white/85 transition-shadow ${selectRing} ${isRoot ? "mx-auto" : ""}`
            : `overflow-visible rounded-lg border border-neutral-600 shadow-sm transition-shadow ${selectRing}`
        }
        style={
          isSequence
            ? {
                backgroundColor: SEQUENCE_SURFACE,
                boxShadow: sequenceDropShadow,
              }
            : {
                backgroundColor: COMPOSITE_CARD_BG,
              }
        }
      >
        <div className="group/header flex items-center gap-2 rounded-t-lg px-2 py-1.5 hover:bg-neutral-800/55">
          <StatusOrSetupIndicator
            status={status}
            needsSetup={needsSetup}
            setupMessage={setupMessage}
          />
          <Tooltip content={compositeRowTooltip} side="top" fullWidth>
            <div
              className="flex min-w-0 flex-1 cursor-pointer items-center gap-2"
              onClick={headerClick}
              role="button"
              tabIndex={0}
              aria-label={compositeRowTooltip}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(node.id);
                }
              }}
            >
              <BtNodeDisc kind={node.kind} size={discSize} largeIcon={discLargeIcon} />
              <span className="flex-1 truncate text-[11px] font-medium text-gray-100">
                {compositeTitle(node)}
                {isRoot ? (
                  <span className="ml-1.5 rounded-sm border border-neutral-600 px-1 py-[1px] text-[9px] uppercase tracking-wide text-gray-400">
                    root
                  </span>
                ) : null}
              </span>
            </div>
          </Tooltip>
          {!isRoot ? <DeleteButton onDelete={() => onDelete(node.id)} /> : null}
        </div>
        <div className="flex flex-col gap-1 overflow-visible px-3 pb-2 pt-1">
          <SlotButton
            onClick={(e) => onSlotClick(node.id, 0, e.currentTarget)}
            variant={children.length === 0 ? "empty" : "between"}
            label="Add step"
            highlightInsert={isActiveInsertSlot(activeInsertSlot, node.id, 0)}
          />
          {onReorderSequenceChildren ? (
            <SortableCompositeChildren
              parentNode={node}
              children={children}
              allNodes={allNodes}
              rootId={rootId}
              nodeStatuses={nodeStatuses}
              activeNodeId={activeNodeId}
              bodiesByUuid={bodiesByUuid}
              incompleteIds={incompleteIds}
              setupMessages={setupMessages}
              onSelect={onSelect}
              onDelete={onDelete}
              onSlotClick={onSlotClick}
              onReorderSequenceChildren={onReorderSequenceChildren}
              activeInsertSlot={activeInsertSlot}
              depth={depth + 1}
            />
          ) : (
            children.map((cid, i) => (
              <React.Fragment key={cid}>
                <BtNodeBlock
                  node={allNodes[cid]}
                  allNodes={allNodes}
                  rootId={rootId}
                  nodeStatuses={nodeStatuses}
                  activeNodeId={activeNodeId}
                  bodiesByUuid={bodiesByUuid}
                  incompleteIds={incompleteIds}
                  setupMessages={setupMessages}
                  onSelect={onSelect}
                  onDelete={onDelete}
                  onSlotClick={onSlotClick}
                  onReorderSequenceChildren={onReorderSequenceChildren}
                  sequenceStepIndex={isSequence ? i + 1 : undefined}
                  activeInsertSlot={activeInsertSlot}
                  depth={depth + 1}
                />
                <SlotButton
                  onClick={(e) => onSlotClick(node.id, i + 1, e.currentTarget)}
                  variant="between"
                  label="Add step"
                  highlightInsert={isActiveInsertSlot(activeInsertSlot, node.id, i + 1)}
                />
              </React.Fragment>
            ))
          )}
        </div>
      </CompositeDroppable>
    );
  }

  if (node.kind === "retry" || node.kind === "repeat") {
    const childId = node.child;
    const isRepeat = node.kind === "repeat";
    const repeatDropShadow = "0 1px 2px 0 rgb(0 0 0 / 0.12)";

    const retryRepeatRowTooltip = `${compositeTitle(node)}${needsSetup ? " · needs configuration" : ""}`;

    return (
      <CompositeDroppable
        compositeId={node.id}
        data-behavior-tree-node="true"
        data-behavior-node-id={node.id}
        className={
          isRepeat
            ? `w-full ${SEQUENCE_MAX_WIDTH_CLASS} overflow-visible rounded-lg border-2 border-dotted border-white/85 transition-shadow ${selectRing} ${isRoot ? "mx-auto" : ""}`
            : `overflow-visible rounded-lg border border-neutral-600 shadow-sm transition-shadow ${selectRing}`
        }
        style={
          isRepeat
            ? {
                backgroundColor: SEQUENCE_SURFACE,
                boxShadow: repeatDropShadow,
              }
            : {
                backgroundColor: COMPOSITE_CARD_BG,
              }
        }
      >
        <div className="group/header flex items-center gap-2 rounded-t-lg px-2 py-1.5 hover:bg-neutral-800/55">
          <StatusOrSetupIndicator
            status={status}
            needsSetup={needsSetup}
            setupMessage={setupMessage}
          />
          <Tooltip content={retryRepeatRowTooltip} side="top" fullWidth>
            <div
              className="flex min-w-0 flex-1 cursor-pointer items-center gap-2"
              onClick={headerClick}
              role="button"
              tabIndex={0}
              aria-label={retryRepeatRowTooltip}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(node.id);
                }
              }}
            >
              <BtNodeDisc kind={node.kind} size={discSize} largeIcon={discLargeIcon} />
              <span className="flex-1 truncate text-[11px] font-medium text-gray-100">
                {compositeTitle(node)}
                {isRoot ? (
                  <span className="ml-1.5 rounded-sm border border-neutral-600 px-1 py-[1px] text-[9px] uppercase tracking-wide text-gray-400">
                    root
                  </span>
                ) : null}
              </span>
            </div>
          </Tooltip>
          {!isRoot ? <DeleteButton onDelete={() => onDelete(node.id)} /> : null}
        </div>
        <div className="overflow-visible px-3 pb-2 pt-1">
          {childId && allNodes[childId] ? (
            <BtNodeBlock
              node={allNodes[childId]}
              allNodes={allNodes}
              rootId={rootId}
              nodeStatuses={nodeStatuses}
              activeNodeId={activeNodeId}
              bodiesByUuid={bodiesByUuid}
              incompleteIds={incompleteIds}
              setupMessages={setupMessages}
              onSelect={onSelect}
              onDelete={onDelete}
              onSlotClick={onSlotClick}
              onReorderSequenceChildren={onReorderSequenceChildren}
              activeInsertSlot={activeInsertSlot}
              depth={depth + 1}
            />
          ) : (
            <SlotButton
              onClick={(e) => onSlotClick(node.id, 0, e.currentTarget)}
              variant="empty"
              label="Add inner step"
              highlightInsert={isActiveInsertSlot(activeInsertSlot, node.id, 0)}
            />
          )}
        </div>
      </CompositeDroppable>
    );
  }

  if (node.kind === "ifThenElse") {
    const slots = [
      { key: "conditionChild", label: "Condition", index: 0, childId: node.conditionChild },
      { key: "thenChild", label: "Then", index: 1, childId: node.thenChild },
      { key: "elseChild", label: "Else (optional)", index: 2, childId: node.elseChild },
    ];
    const rowTooltip = `${compositeTitle(node)}${needsSetup ? " · needs configuration" : ""}`;
    return (
      <CompositeDroppable
        compositeId={node.id}
        data-behavior-tree-node="true"
        data-behavior-node-id={node.id}
        className={`overflow-visible rounded-lg border border-neutral-600 shadow-sm transition-shadow ${selectRing}`}
        style={{ backgroundColor: COMPOSITE_CARD_BG }}
      >
        <div className="group/header flex items-center gap-2 rounded-t-lg px-2 py-1.5 hover:bg-neutral-800/55">
          <StatusOrSetupIndicator
            status={status}
            needsSetup={needsSetup}
            setupMessage={setupMessage}
          />
          <Tooltip content={rowTooltip} side="top" fullWidth>
            <div
              className="flex min-w-0 flex-1 cursor-pointer items-center gap-2"
              onClick={headerClick}
              role="button"
              tabIndex={0}
              aria-label={rowTooltip}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(node.id);
                }
              }}
            >
              <BtNodeDisc kind={node.kind} size={discSize} largeIcon={discLargeIcon} />
              <span className="flex-1 truncate text-[11px] font-medium text-gray-100">
                {compositeTitle(node)}
                {isRoot ? (
                  <span className="ml-1.5 rounded-sm border border-neutral-600 px-1 py-[1px] text-[9px] uppercase tracking-wide text-gray-400">
                    root
                  </span>
                ) : null}
              </span>
            </div>
          </Tooltip>
          {!isRoot ? <DeleteButton onDelete={() => onDelete(node.id)} /> : null}
        </div>
        <div className="space-y-2 overflow-visible px-3 pb-2 pt-1">
          {slots.map((slot) => (
            <div key={slot.key} className="space-y-1">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                {slot.label}
              </div>
              {slot.childId && allNodes[slot.childId] ? (
                <BtNodeBlock
                  node={allNodes[slot.childId]}
                  allNodes={allNodes}
                  rootId={rootId}
                  nodeStatuses={nodeStatuses}
                  activeNodeId={activeNodeId}
                  bodiesByUuid={bodiesByUuid}
                  incompleteIds={incompleteIds}
                  setupMessages={setupMessages}
                  onSelect={onSelect}
                  onDelete={onDelete}
                  onSlotClick={onSlotClick}
                  onReorderSequenceChildren={onReorderSequenceChildren}
                  activeInsertSlot={activeInsertSlot}
                  depth={depth + 1}
                />
              ) : (
                <SlotButton
                  onClick={(e) => onSlotClick(node.id, slot.index, e.currentTarget)}
                  variant="empty"
                  label={`Add ${slot.label.toLowerCase()}`}
                  highlightInsert={isActiveInsertSlot(activeInsertSlot, node.id, slot.index)}
                />
              )}
            </div>
          ))}
        </div>
      </CompositeDroppable>
    );
  }

  // Leaf
  const summary = leafSummary(node, bodiesByUuid);
  const leafRowTooltip = `${nodeTitle(node)}${needsSetup ? " · needs configuration" : ""}`;
  return (
    <div
      data-behavior-tree-node="true"
      data-behavior-node-id={node.id}
      className={`group/header flex items-center gap-2 rounded-md border border-neutral-600 px-2 py-1.5 transition-colors hover:bg-neutral-800/65 ${selectRing}`}
      style={{
        backgroundColor: LEAF_ROW_BG,
      }}
    >
      <StatusOrSetupIndicator status={status} needsSetup={needsSetup} setupMessage={setupMessage} />
      <Tooltip content={leafRowTooltip} side="top" fullWidth>
        <div
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2"
          onClick={headerClick}
          role="button"
          tabIndex={0}
          aria-label={leafRowTooltip}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSelect(node.id);
            }
          }}
        >
          <BtNodeDisc kind={node.kind} size={discSize} largeIcon={discLargeIcon} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[11px] font-medium text-gray-100">{nodeTitle(node)}</div>
            {summary ? <div className="truncate text-[10px] text-gray-400">{summary}</div> : null}
          </div>
          {category === "condition" ? (
            <span className="shrink-0 rounded-sm border border-emerald-500/30 bg-emerald-500/10 px-1 py-[1px] text-[9px] uppercase tracking-wide text-emerald-300">
              cond
            </span>
          ) : null}
        </div>
      </Tooltip>
      {!isRoot ? <DeleteButton onDelete={() => onDelete(node.id)} /> : null}
    </div>
  );
}
